import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import { useIntroTextOptions } from '@/src/renderer/components/common/IntroTip'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { LifeSpace } from '@/src/renderer/lib/db/models'
import { INTRO_SETTING, useIntroOffer, type IntroOffer } from '@/src/renderer/lib/intro'
import { writeLearnTasks } from '../learnTasks'
import './IntroOfferCard.scss'

/**
 * **Angebot der Lernaufgaben** (#323, Variante G) in Kurz notiert, solange es offen ist: „Ja, gern“
 * legt die Lernaufgaben an, „Nein danke“ schließt die Karte. Beides gilt für dieses Gerät.
 *
 * `layout`: `row` über der Liste (Text links, Buttons rechts), `stack` im leeren Zustand an Stelle seiner
 * Buttons (untereinander, mittig) — wie `actionsLayout` am `EmptyState`.
 *
 * @example
 * <IntroOfferCard lifeSpace="personal" />
 * <IntroOfferCard lifeSpace="personal" layout="stack" />
 */
export function IntroOfferCard({ lifeSpace, layout = 'row' }: { lifeSpace: LifeSpace; layout?: 'row' | 'stack' }) {
  const { t } = useTranslation()
  const source = useDataSource()
  const offer = useIntroOffer()
  const { web, quickAddKeys } = useIntroTextOptions()
  const [busy, setBusy] = useState(false)

  if (offer !== 'pending') return null

  const answer = async (yes: boolean) => {
    setBusy(true)
    try {
      // Erst die Antwort, dann die Aufgaben: Mit der ersten ist Kurz notiert nicht mehr leer, und über der Liste
      // stünde das Angebot sonst noch einmal — mit Buttons, die ein zweites Mal anlegten.
      await source.setSetting<IntroOffer>(INTRO_SETTING.offer, yes ? 'accepted' : 'declined')
      if (yes) await writeLearnTasks(source, { t, lifeSpace, web, quickAddKeys })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`intro-offer-card${layout === 'stack' ? ' intro-offer-card--stack' : ''}`}>
      <p className="intro-offer-card__text">{t('intro.offer.text')}</p>
      <div className="intro-offer-card__actions">
        <Button variant="primary" size="sm" disabled={busy} onClick={() => void answer(true)}>{t('intro.offer.yes')}</Button>
        <Button variant="tertiary" size="sm" disabled={busy} onClick={() => void answer(false)}>{t('intro.offer.no')}</Button>
      </div>
    </div>
  )
}
